import { useEffect, useId } from 'react';
import { Control, FieldValues, useFormState } from 'react-hook-form';
import { useUnsavedChangesContext } from './_private/UnsavedChangesContext';

interface UseRegisterDirtyStateOptions {
  /**
   * Custom message for this dirty state
   */
  message?: string;
}

/**
 * Hook that registers/unregisters dirty state in the UnsavedChangesContext.
 * Automatically handles registration/unregistration in useEffect.
 * Uses useId to generate a unique identifier for this dirty state.
 *
 * @param isDirty - Whether this component/form has unsaved changes
 * @param options - Optional configuration
 */
export const useRegisterDirtyState = (
  isDirty: boolean,
  options: UseRegisterDirtyStateOptions = {}
) => {
  const { message } = options;
  const { registerDirty, unregisterDirty } = useUnsavedChangesContext();
  const id = useId();

  useEffect(() => {
    if (isDirty) {
      registerDirty(id, message);
    } else {
      unregisterDirty(id);
    }

    // Cleanup on unmount
    return () => {
      unregisterDirty(id);
    };
  }, [isDirty, message, id, registerDirty, unregisterDirty]);

  return { id };
};

/**
 * Registers RHF form dirty state into UnsavedChangesContext via a narrow
 * `useFormState({ control })` subscription (avoids re-rendering on every keystroke
 * from reading `form.formState` on a parent).
 *
 * Render inside {@link UnsavedChangesProtection} or
 * {@link PageUnsavedChangesProtection}. Prefer this for a single RHF form;
 * use {@link useRegisterDirtyState} for nested/extra dirty sources.
 */
export function RegisterFormDirtyState<TFieldValues extends FieldValues>({
  control,
  message
}: {
  control: Control<TFieldValues>;
  message?: string;
}) {
  const { isDirty } = useFormState({ control });
  useRegisterDirtyState(isDirty, { message });
  return null;
}
